import { ensureSupabaseConfigured, isSupabaseConfigured, supabase } from '../lib/supabase';

export type ReportReason =
  | 'prohibited_item'
  | 'counterfeit'
  | 'misleading_description'
  | 'fraud_scam'
  | 'stolen_item'
  | 'hate_harassment'
  | 'adult_content'
  | 'other';

export interface ProductReportInput {
  reporterUserId: string;
  sellerUserId: string;
  productId: string;
  productTitle?: string;
  productUrl?: string;
  subject: string;
  reason: ReportReason;
  message: string;
}

export interface ConversationReportInput {
  reporterUserId: string;
  sellerUserId: string;
  conversationId: string;
  subject: string;
  reason: ReportReason;
  message: string;
}

export interface ProductReportRecord extends ProductReportInput {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: 'submitted';
}

export interface ConversationReportRecord extends ConversationReportInput {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: 'submitted';
}

const REPORT_INDEX_KEY = 'product_reports:index';
const REPORT_ITEM_KEY_PREFIX = 'product_reports:item:';

const safeJsonParse = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const generateId = (): string => {
  try {
    const c: any = (globalThis as any).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch {
    // ignore
  }
  return `rep_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

export class ReportService {
  static async submitProductReport(input: ProductReportInput): Promise<ProductReportRecord> {
    if (!input.reporterUserId) throw new Error('Missing reporter user id');
    if (!input.sellerUserId) throw new Error('Missing seller user id');
    if (!input.productId) throw new Error('Missing product id');
    if (!input.subject?.trim()) throw new Error('Missing subject');
    if (!input.reason) throw new Error('Missing reason');
    if (!input.message?.trim()) throw new Error('Missing message');

    if (isSupabaseConfigured()) {
      ensureSupabaseConfigured();
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUser = sessionData?.session?.user;
      if (!sessionUser) {
        throw new Error('User must be authenticated. Your session is missing/expired — please sign in again and retry.');
      }
      if (sessionUser.id !== input.reporterUserId) {
        throw new Error('Authenticated user mismatch while submitting report');
      }

      const payload = {
        reporter_id: input.reporterUserId,
        seller_id: input.sellerUserId,
        product_id: input.productId,
        subject: input.subject.trim(),
        reason: input.reason,
        details: input.message.trim(),
      };

      const { data, error } = await supabase
        .from('product_reports')
        .insert(payload)
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to save report');
      }
      if (!data) {
        throw new Error('Failed to save report');
      }

      return {
        id: String((data as any).id),
        reporterUserId: String((data as any).reporter_id),
        sellerUserId: String((data as any).seller_id),
        productId: String((data as any).product_id),
        productTitle: input.productTitle,
        productUrl: input.productUrl,
        subject: String((data as any).subject),
        reason: (data as any).reason as ReportReason,
        message: String((data as any).details),
        createdAt: String((data as any).created_at),
        updatedAt: String((data as any).updated_at),
        status: String((data as any).status || 'submitted') as any,
      };
    }

    const now = new Date().toISOString();
    const record: ProductReportRecord = {
      id: generateId(),
      createdAt: now,
      updatedAt: now,
      status: 'submitted',
      ...input,
      subject: input.subject.trim(),
      message: input.message.trim(),
    };

    const index = safeJsonParse<string[]>(localStorage.getItem(REPORT_INDEX_KEY), []);
    const nextIndex = Array.from(new Set([record.id, ...index]));
    localStorage.setItem(REPORT_INDEX_KEY, JSON.stringify(nextIndex));
    localStorage.setItem(REPORT_ITEM_KEY_PREFIX + record.id, JSON.stringify(record));

    const endpoint = (process.env.REACT_APP_REPORT_PRODUCT_ENDPOINT as string | undefined) || '';
    if (endpoint) {
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(record),
        });
        if (!res.ok) {
          throw new Error(`${res.status} ${res.statusText}`);
        }
      } catch {
        // keep local record as fallback
      }
    }

    return record;
  }

  static getReportById(id: string): ProductReportRecord | null {
    if (!id) return null;
    const raw = localStorage.getItem(REPORT_ITEM_KEY_PREFIX + id);
    return safeJsonParse<ProductReportRecord | null>(raw, null);
  }

  static listReports(): ProductReportRecord[] {
    const ids = safeJsonParse<string[]>(localStorage.getItem(REPORT_INDEX_KEY), []);
    return ids
      .map((id) => this.getReportById(id))
      .filter((r): r is ProductReportRecord => !!r);
  }

  static async submitConversationReport(input: ConversationReportInput): Promise<ConversationReportRecord> {
    if (!input.reporterUserId) throw new Error('Missing reporter user id');
    if (!input.sellerUserId) throw new Error('Missing seller user id');
    if (!input.conversationId) throw new Error('Missing conversation id');
    if (!input.subject?.trim()) throw new Error('Missing subject');
    if (!input.reason) throw new Error('Missing reason');
    if (!input.message?.trim()) throw new Error('Missing message');

    if (isSupabaseConfigured()) {
      ensureSupabaseConfigured();
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUser = sessionData?.session?.user;
      if (!sessionUser) {
        throw new Error('User must be authenticated. Your session is missing/expired — please sign in again and retry.');
      }
      if (sessionUser.id !== input.reporterUserId) {
        throw new Error('Authenticated user mismatch while submitting report');
      }

      const payload = {
        reporter_id: input.reporterUserId,
        seller_id: input.sellerUserId,
        product_id: null,
        conversation_id: input.conversationId,
        subject: input.subject.trim(),
        reason: input.reason,
        details: input.message.trim(),
      };

      const { data, error } = await supabase
        .from('product_reports')
        .insert(payload)
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to save report');
      }
      if (!data) {
        throw new Error('Failed to save report');
      }

      return {
        id: String((data as any).id),
        reporterUserId: String((data as any).reporter_id),
        sellerUserId: String((data as any).seller_id),
        conversationId: String((data as any).conversation_id),
        subject: String((data as any).subject),
        reason: (data as any).reason as ReportReason,
        message: String((data as any).details),
        createdAt: String((data as any).created_at),
        updatedAt: String((data as any).updated_at),
        status: String((data as any).status || 'submitted') as any,
      };
    }

    const now = new Date().toISOString();
    return {
      id: generateId(),
      createdAt: now,
      updatedAt: now,
      status: 'submitted',
      ...input,
      subject: input.subject.trim(),
      message: input.message.trim(),
    };
  }
}
